import { mapState } from "vuex";
import { importScript } from "@/utils/scriptUtil";
import _ from "lodash";
import { isMobile } from "@/utils/utils";

const EQUALWEB_CLOSE_FALLBACK_KEY = "__omintEqualwebCloseFallback";
const EQUALWEB_CLOSE_FALLBACK_SIZE = 64;
const EQUALWEB_CLOSE_FALLBACK_EVENTS = ["pointerdown", "click"];
const EQUALWEB_MENU_OPEN_CLASS = "INDmenu-open";
const EQUALWEB_SHADOW_HOST_ID = "INDShadowRootHost";
const EQUALWEB_SHADOW_WRAP_ID = "INDshadowRootWrap";
const VLIBRAS_CONTAINER_ID = "vlibras";
const VLIBRAS_ACCESS_BUTTON_ID = "vlibrasclick";
const VLIBRAS_ACCESS_WRAPPER_ID = "vlibras-access-wrapper";
const VLIBRAS_MENU_BUTTON_ID = "btvlibras";
const VLIBRAS_MENU_WRAP_ID = "INDmenuBtnzWrap";
const VLIBRAS_SCRIPT_SRC = "https://vlibras.gov.br/app/vlibras-plugin.js";
const VLIBRAS_WIDGET_SRC = "https://vlibras.gov.br/app";
const VLIBRAS_HIDE_ACCESS_BUTTON_STYLE_ID = "omint-vlibras-hide-access-button";
const VLIBRAS_LOADING_KEY = "__omintVlibrasLoading";
const VLIBRAS_INITIALIZED_KEY = "__omintVlibrasInitialized";
const VLIBRAS_INTERVAL_KEY = "__omintVlibrasMenuInterval";
const VLIBRAS_BUTTON_READY_KEY = "__omintVlibrasMenuButtonReady";

export default {
  computed: {
    ...mapState("saude", ["username"]),
    isMobile() {
      return isMobile();
    },
    defaultEqualwebButtonStyle() {
      return {
        vPosition: ["20%", "20"],
        margin: ["0", "0"],
        scale: ["0.5", "0.6"],
        color: {
          main: "#102a46",
          second: "#ffffff"
        },
        icon: {
          type: 1,
          shape: "circle",
          outline: false,
          outlineColor: "#ffffff"
        }
      };
    }
  },
  methods: {
    isValidEqualwebUser() {
      const username = process.env.VUE_APP_EQUALWEB_USER;
      if (this.isMobile || username) {
        const equalWrap = document.getElementById("INDWrap");
        if (username && username !== this.username) {
          if (equalWrap) equalWrap.style["cssText"] = "display: none !important;";
          return false;
        }
        if (equalWrap?.style["cssText"]) {
          equalWrap.style["cssText"] = "";
        }
      }
      return true;
    },
    resizeEqualweb: _.throttle(function (tentativa) {
      const equalMenu = document.getElementById("INDmenu-btn");
      if (!equalMenu?.style["cssText"]) {
        tentativa++;
        if (tentativa < 50) setTimeout(() => this.resizeEqualweb(tentativa), 100);
        return;
      }
      if (this.equalButtonStyle) equalMenu.style["cssText"] = this.equalButtonStyle;
      if (!this.isValidEqualwebUser()) return;
    }, 100),
    ensureEqualwebCloseFallback() {
      const win = globalThis.window;
      if (win[EQUALWEB_CLOSE_FALLBACK_KEY]) return;

      const closeFallback = event => {
        if (event.clientX > EQUALWEB_CLOSE_FALLBACK_SIZE || event.clientY > EQUALWEB_CLOSE_FALLBACK_SIZE) return;

        const path = event.composedPath?.() || [];
        const topElement = document.elementFromPoint?.(event.clientX, event.clientY);
        const isEqualwebClick = [...path, topElement].some(
          element => element?.id === EQUALWEB_SHADOW_HOST_ID || element?.id === EQUALWEB_SHADOW_WRAP_ID
        );
        const isEqualwebOpen = document.body.classList.contains(EQUALWEB_MENU_OPEN_CLASS);
        if (!isEqualwebClick && !isEqualwebOpen) return;

        setTimeout(() => {
          win.interdeal?.CloseMenu?.();
          document.body.classList.remove(EQUALWEB_MENU_OPEN_CLASS);
          win.interdeal?.a11y?.a11yBtn?.setAttribute?.("aria-expanded", "false");
        }, 0);
      };

      win[EQUALWEB_CLOSE_FALLBACK_KEY] = closeFallback;
      EQUALWEB_CLOSE_FALLBACK_EVENTS.forEach(eventName => {
        document.addEventListener(eventName, closeFallback, true);
      });
    },
    hideVlibrasAccessButton() {
      document
        .querySelectorAll(`[vw-access-button], #${VLIBRAS_ACCESS_BUTTON_ID}, #${VLIBRAS_ACCESS_WRAPPER_ID}`)
        .forEach(accessButton => {
          accessButton.style.setProperty("display", "none", "important");
          accessButton.style.setProperty("visibility", "hidden", "important");
          accessButton.style.setProperty("pointer-events", "none", "important");
        });
    },
    ensureVlibrasAccessButtonStyle() {
      if (document.getElementById(VLIBRAS_HIDE_ACCESS_BUTTON_STYLE_ID)) return;

      const style = document.createElement("style");
      style.id = VLIBRAS_HIDE_ACCESS_BUTTON_STYLE_ID;
      style.appendChild(
        document.createTextNode(
          `[vw-access-button], #${VLIBRAS_ACCESS_BUTTON_ID}, #${VLIBRAS_ACCESS_WRAPPER_ID} { display: none !important; visibility: hidden !important; pointer-events: none !important; }`
        )
      );
      document.head.appendChild(style);
    },
    ensureVlibrasContainer() {
      this.ensureVlibrasAccessButtonStyle();

      if (document.getElementById(VLIBRAS_CONTAINER_ID)) return;

      const vlibras = document.createElement("div");
      vlibras.setAttribute("vw", "");
      vlibras.className = "enabled";
      vlibras.id = VLIBRAS_CONTAINER_ID;
      vlibras.style.display = "none";

      const accessButton = document.createElement("div");
      accessButton.setAttribute("vw-access-button", "");
      accessButton.className = "active";
      accessButton.id = VLIBRAS_ACCESS_BUTTON_ID;
      accessButton.style.setProperty("display", "none", "important");

      const pluginWrapper = document.createElement("div");
      pluginWrapper.setAttribute("vw-plugin-wrapper", "");

      const topWrapper = document.createElement("div");
      topWrapper.className = "vw-plugin-top-wrapper";

      pluginWrapper.appendChild(topWrapper);
      vlibras.appendChild(accessButton);
      vlibras.appendChild(pluginWrapper);
      document.body.appendChild(vlibras);
      this.hideVlibrasAccessButton();
    },
    loadVlibrasScript() {
      const win = globalThis.window;
      if (win.VLibras?.Widget) return Promise.resolve();

      const currentScript = Array.from(document.scripts).find(script => script.src === VLIBRAS_SCRIPT_SRC);
      if (currentScript) {
        return new Promise((resolve, reject) => {
          currentScript.addEventListener("load", resolve, { once: true });
          currentScript.addEventListener("error", reject, { once: true });
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = VLIBRAS_SCRIPT_SRC;
        script.addEventListener("load", resolve, { once: true });
        script.addEventListener("error", reject, { once: true });
        document.body.appendChild(script);
      });
    },
    appendVlibrasMenuButton(menu) {
      if (!menu || menu.querySelector(`#${VLIBRAS_MENU_BUTTON_ID}`)) return;

      const button = document.createElement("button");
      button.setAttribute("id", VLIBRAS_MENU_BUTTON_ID);
      button.setAttribute("data-indopt", "vlibrasreader");
      button.setAttribute("role", "checkbox");
      button.setAttribute("aria-labelledby", "vlibras_label_1 vlibras_label_2");
      button.setAttribute("tabindex", "0");
      button.setAttribute("aria-checked", "false");
      button.innerHTML =
        '<svg version="1.2" baseProfile="tiny" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="126.04px" height="122.666px" viewBox="0 0 126.04 122.666" xml:space="preserve"><path d="M108.875,61.237c0.002-10.126-4.098-19.334-10.711-25.996c-1.361-1.374-3.58-1.383-4.951-0.021c-1.372,1.361-1.381,3.577-0.02,4.951v-0.002c5.371,5.421,8.681,12.847,8.681,21.068c0.003,0.016,0,0.074,0.003,0.17c-0.032,8.219-3.401,15.663-8.842,21.071c-1.372,1.361-1.379,3.577-0.018,4.949c0.686,0.688,1.585,1.034,2.484,1.034c0.893,0,1.784-0.339,2.467-1.018c6.695-6.646,10.873-15.881,10.906-26.063V61.237z M109.015,19.872c-1.364-1.372-3.579-1.381-4.952-0.019c-1.369,1.363-1.378,3.579-0.016,4.951v-0.002c9.273,9.353,14.992,22.19,14.992,36.389c0,0.049,0,0.134,0.002,0.253c-0.058,14.206-5.878,27.071-15.267,36.398c-1.372,1.362-1.381,3.58-0.017,4.952c0.684,0.689,1.584,1.034,2.484,1.034c0.891,0,1.781-0.338,2.465-1.016c10.648-10.569,17.273-25.227,17.332-41.405v-0.217C126.042,45.092,119.536,30.468,109.015,19.872z M81.307,0.362c-1.189-0.59-2.621-0.451-3.677,0.355L35.889,32.576H3.502c-0.924,0-1.824,0.372-2.476,1.025C0.375,34.253,0,35.153,0,36.075v50.516c0,0.922,0.375,1.822,1.026,2.476c0.651,0.651,1.554,1.024,2.476,1.024H35.89l41.74,31.858c0.622,0.474,1.372,0.717,2.128,0.717c0.527,0,1.059-0.119,1.549-0.361c1.189-0.59,1.947-1.81,1.947-3.136V3.5C83.254,2.17,82.497,0.949,81.307,0.362z M76.255,112.092L39.196,83.809c-0.606-0.464-1.36-0.718-2.122-0.718H7V39.575h30.074c0.762,0,1.516-0.255,2.122-0.717l37.059-28.286V112.092z"></path></svg><span id="vlibras_label_1" aria-hidden="true" class="INDmenuBtn-text">V Libras</span><span id="vlibras_label_2" aria-hidden="true" class="INDmenuBtn-desc">Aciona o V Libras</span>';

      button.addEventListener("click", () => {
        const win = globalThis.window;
        const vlibras = document.getElementById(VLIBRAS_CONTAINER_ID);
        const vlibrasButton = document.getElementById(VLIBRAS_ACCESS_BUTTON_ID);
        if (vlibras) vlibras.style.display = "block";
        if (win.VLibrasWidget?.open) {
          win.VLibrasWidget.open();
        } else if (vlibrasButton) {
          vlibrasButton.click();
        }
        this.hideVlibrasAccessButton();
        setTimeout(() => this.hideVlibrasAccessButton(), 0);
      });

      menu.appendChild(button);
    },
    injectVlibrasMenuButton() {
      const win = globalThis.window;
      if (win[VLIBRAS_BUTTON_READY_KEY] || win[VLIBRAS_INTERVAL_KEY]) return;

      const tryAppend = () => {
        try {
          const menu = win.interdeal?.menu?.querySelector?.(`#${VLIBRAS_MENU_WRAP_ID}`);
          if (!menu) return false;

          this.appendVlibrasMenuButton(menu);
          win[VLIBRAS_BUTTON_READY_KEY] = true;
          return true;
        } catch (error) {
          return false;
        }
      };

      if (tryAppend()) return;

      win[VLIBRAS_INTERVAL_KEY] = setInterval(() => {
        if (tryAppend()) {
          clearInterval(win[VLIBRAS_INTERVAL_KEY]);
          win[VLIBRAS_INTERVAL_KEY] = null;
        }
      }, 1000);
    },
    async initVlibras() {
      const win = globalThis.window;
      if (!document.body) return;

      this.ensureVlibrasContainer();

      if (win[VLIBRAS_LOADING_KEY]) {
        await win[VLIBRAS_LOADING_KEY];
        if (win[VLIBRAS_INITIALIZED_KEY]) this.injectVlibrasMenuButton();
        return;
      }

      if (!win[VLIBRAS_INITIALIZED_KEY]) {
        win[VLIBRAS_LOADING_KEY] = this.loadVlibrasScript()
          .then(() => {
            if (!win[VLIBRAS_INITIALIZED_KEY] && win.VLibras?.Widget) {
              new win.VLibras.Widget(VLIBRAS_WIDGET_SRC);
              win[VLIBRAS_INITIALIZED_KEY] = true;
            }
            this.hideVlibrasAccessButton();
            setTimeout(() => this.hideVlibrasAccessButton(), 100);
          })
          .catch(error => {
            console.error("Nao foi possivel carregar o VLibras.", error);
          })
          .finally(() => {
            win[VLIBRAS_LOADING_KEY] = null;
          });

        await win[VLIBRAS_LOADING_KEY];
      }

      if (win[VLIBRAS_INITIALIZED_KEY]) this.injectVlibrasMenuButton();
    },
    async initEqualweb(btnStyle = this.defaultEqualwebButtonStyle) {
      if (!this.isValidEqualwebUser()) return;
      const siteKey = process.env.VUE_APP_EQUALWEB_KEY;
      const domainsJS = process.env.VUE_APP_EQUALWEB_JS;
      const domainsACC = process.env.VUE_APP_EQUALWEB_ACC;
      const siteIntegrity = process.env.VUE_APP_EQUALWEB_INTEGRITY;
      const siteSRC = process.env.VUE_APP_EQUALWEB_SRC;
      if (!siteKey || !domainsJS || !domainsACC || !siteIntegrity || !siteSRC) return;
      if (globalThis.window.interdeal) {
        this.ensureEqualwebCloseFallback();
        this.resizeEqualweb(0);
        await this.initVlibras();
        return;
      }
      globalThis.window.interdeal = {
        sitekey: siteKey,
        Position: "left",
        Menulang: "PT",
        draggable: true,
        domains: {
          js: domainsJS,
          acc: domainsACC
        },
        btnStyle: btnStyle
      };
      await importScript(document, siteSRC, coreCall => {
        coreCall.defer = true;
        coreCall.integrity = siteIntegrity;
        coreCall.crossOrigin = "anonymous";
        coreCall.dataset.cfasync = "true";
      });
      this.ensureEqualwebCloseFallback();
      this.resizeEqualweb(0);
      await this.initVlibras();
    }
  }
};
